/**
 * Buzzycart cart extras — "You might also like" carousel in the cart drawer.
 * Layout 1:1 after the Zorenza/UpCart cart upsell (horizontal, one card per view,
 * image left, bold title, price, full-width dark "Add" button, chevrons left/right).
 * Adds through the same path as Horizon's product form (CartLinesUpdateEvent + sections),
 * so the drawer morphs itself after adding.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const SECTION_ID = 'cart-drawer-section';

class BuzzycartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-ids'];

  #loadedFor = null;
  #items = [];
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#load();
  }

  get #cartIds() {
    return (this.dataset.productIds || '').split(',').map((s) => s.trim()).filter(Boolean);
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const n = Number(cents) / 100;
    const two = n.toFixed(2);
    return fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(n).toLocaleString('de-DE'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, two.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(n)))
      .replace(/\{\{\s*amount\s*\}\}/, two)
      .replace(/<[^>]+>/g, '');
  }

  #img(url) {
    if (!url) return '';
    const clean = url.split('?')[0];
    return `${clean}?width=240`;
  }

  async #load() {
    const ids = this.#cartIds;
    if (!ids.length) {
      this.style.display = 'none';
      return;
    }
    const key = ids.join(',');
    if (key === this.#loadedFor) return;
    this.#loadedFor = key;

    let products = [];
    try {
      const r = await fetch(`${this.dataset.recsUrl}.json?product_id=${ids[0]}&limit=10&intent=related`);
      if (r.ok) products = (await r.json()).products || [];
    } catch (e) {
      /* stil: fallback hieronder */
    }
    products = products.filter((p) => p.available && !ids.includes(String(p.id)));

    if (products.length < 3) {
      try {
        const r = await fetch('/collections/all/products.json?limit=24');
        const extra = ((await r.json()).products || []).map((p) => ({
          id: p.id,
          title: p.title,
          handle: p.handle,
          url: `/products/${p.handle}`,
          featured_image: p.images && p.images[0] ? p.images[0].src : '',
          price: Math.round(parseFloat((p.variants && p.variants[0] && p.variants[0].price) || '0') * 100),
          variants: (p.variants || []).map((v) => ({ id: v.id, available: v.available })),
          available: (p.variants || []).some((v) => v.available),
        }));
        for (const p of extra) {
          if (p.available && !ids.includes(String(p.id)) && !products.some((q) => q.id === p.id)) products.push(p);
        }
      } catch (e) {
        /* stil */
      }
    }

    this.#items = products.slice(0, 10);
    if (!this.#items.length) {
      this.style.display = 'none';
      return;
    }
    this.style.display = '';
    this.#render();
  }

  #render() {
    const addText = this.dataset.addText || 'Add';
    const cards = this.#items
      .map((p) => {
        const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
        if (!variant) return '';
        return `
        <div class="bc-upsell__card" data-product-id="${p.id}">
          <a class="bc-upsell__imgwrap" href="${p.url}" tabindex="-1" aria-hidden="true">
            <img class="bc-upsell__img" src="${this.#img(p.featured_image)}" alt="" loading="lazy" width="110" height="110">
          </a>
          <div class="bc-upsell__body">
            <a class="bc-upsell__name" href="${p.url}">${this.#esc(p.title)}</a>
            <div class="bc-upsell__price">${this.#money(p.price)}</div>
            <button type="button" class="bc-upsell__add" data-variant-id="${variant.id}">${this.#esc(addText)}</button>
          </div>
        </div>`;
      })
      .join('');
    const multi = this.#items.length > 1;
    // pijlen ONDER de kaart (gecentreerd): aan de zijkanten botsten ze met de zwevende app-badge rechts
    this.innerHTML = `
      <p class="bc-upsell__title">${this.#esc(this.dataset.title || 'You might also like')}</p>
      <div class="bc-upsell__track">${cards}</div>
      <div class="bc-upsell__navrow"${multi ? '' : ' hidden'}>
        <button type="button" class="bc-upsell__nav" data-dir="-1" aria-label="Previous">&#8249;</button>
        <span class="bc-upsell__count"><span data-current>1</span> / ${this.#items.length}</span>
        <button type="button" class="bc-upsell__nav" data-dir="1" aria-label="Next">&#8250;</button>
      </div>`;
    const track = this.querySelector('.bc-upsell__track');
    track?.addEventListener('scroll', () => {
      const card = track.querySelector('.bc-upsell__card');
      if (!card) return;
      const idx = Math.round(track.scrollLeft / (card.getBoundingClientRect().width + 8)) + 1;
      const el = this.querySelector('[data-current]');
      if (el) el.textContent = String(Math.min(Math.max(idx, 1), this.#items.length));
    }, { passive: true });
  }

  #esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  #onClick = (event) => {
    const nav = event.target.closest('.bc-upsell__nav');
    if (nav) {
      const track = this.querySelector('.bc-upsell__track');
      if (!track) return;
      const card = track.querySelector('.bc-upsell__card');
      const step = card ? card.getBoundingClientRect().width + 8 : track.clientWidth;
      const dir = Number(nav.dataset.dir);
      const atEnd = dir > 0 && track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      const atStart = dir < 0 && track.scrollLeft <= 4;
      if (atEnd) track.scrollTo({ left: 0, behavior: 'smooth' });
      else if (atStart) track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
      else track.scrollBy({ left: dir * step, behavior: 'smooth' });
      return;
    }
    const btn = event.target.closest('.bc-upsell__add');
    if (btn) this.#add(btn);
  };

  async #add(btn) {
    if (this.#busy) return;
    this.#busy = true;
    const variantId = btn.dataset.variantId;
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = this.dataset.addingText || 'Adding…';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', SECTION_ID);

    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-upsell',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise,
      })
    );

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...cfg,
        headers: { ...cfg.headers, Accept: 'text/html' },
      }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message || 'Add failed');
      const ajaxCart = await fetch(Theme.routes.cart_url + '.js').then((r) => r.json());
      resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { sections: response.sections },
      });
      btn.textContent = this.dataset.addedText || 'Added ✓';
    } catch (e) {
      reject(e);
      btn.disabled = false;
      btn.textContent = label;
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('buzzycart-upsell')) customElements.define('buzzycart-upsell', BuzzycartUpsell);
